import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Package, Wrench, AlertTriangle, CheckCircle,
  Calendar, TrendingUp, RefreshCw, ChevronRight
} from 'lucide-react';
import { formatDate } from '../utils/dateUtils';

import { authHeaders } from '../lib/authToken';
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  disponible: { label: 'Disponible', color: '#3DDC97', bg: 'rgba(61,220,151,0.12)', icon: <CheckCircle size={18} /> },
  sorti: { label: 'Sorti', color: 'var(--admin-gold)', bg: 'rgba(212,175,55,0.12)', icon: <TrendingUp size={18} /> },
  maintenance: { label: 'Maintenance', color: '#FFCC66', bg: 'rgba(255,204,102,0.12)', icon: <Wrench size={18} /> },
  hors_service: { label: 'Hors service', color: '#FF5C6C', bg: 'rgba(255,92,108,0.12)', icon: <AlertTriangle size={18} /> },
  reserve: { label: 'Réservé', color: '#C5D8F9', bg: 'rgba(197,216,249,0.12)', icon: <Calendar size={18} /> },
};

export default function AdminMateriel() {
  const [dashboard, setDashboard] = useState(null);
  const [products, setProducts] = useState([]);
  const [calendar, setCalendar] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [updating, setUpdating] = useState(null);
  const [conflitCheck, setConflitCheck] = useState({ date: '', results: null });

  const now = new Date();

  useEffect(() => {
    fetchDashboard();
    fetchCalendar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function fetchDashboard() {
    setLoading(true);
    try {
      const [dashRes, prodRes] = await Promise.all([
        fetch(`${BACKEND}/api/materiel/dashboard`, { headers: authHeaders() }),
        fetch(`${BACKEND}/api/products?limit=50&product_type=location`)
      ]);
      const [dashData, prodData] = await Promise.all([dashRes.json(), prodRes.json()]);
      setDashboard(dashData);
      setProducts(prodData.products || []);
    } catch (err) { if (process.env.NODE_ENV !== 'production') console.error(err); }
    setLoading(false);
  }

  async function fetchCalendar(mois, annee) {
    try {
      const m = mois || now.getMonth() + 1;
      const a = annee || now.getFullYear();
      const res = await fetch(`${BACKEND}/api/materiel/calendrier?mois=${m}&annee=${a}`, { headers: authHeaders() });
      const data = await res.json();
      setCalendar(data);
    } catch (err) { if (process.env.NODE_ENV !== 'production') console.error(err); }
  }

  async function updateStatus(productId, newStatus, raison = '') {
    setUpdating(productId);
    try {
      await fetch(`${BACKEND}/api/materiel/${productId}/statut`, {
        method: 'PUT',
        headers: authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ statut: newStatus, raison })
      });
      fetchDashboard();
    } catch (err) { if (process.env.NODE_ENV !== 'production') console.error(err); }
    setUpdating(null);
  }

  async function checkConflits(date) {
    if (!date) return;
    const productIds = products.map(p => p.id);
    const res = await fetch(`${BACKEND}/api/materiel/verifier-conflit`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ date_evenement: date, product_ids: productIds })
    });
    const data = await res.json();
    setConflitCheck({ date, results: data });
  }

  const TABS = [
    { id: 'dashboard', label: 'Dashboard' },
    { id: 'products', label: 'Matériel' },
    { id: 'calendar', label: 'Calendrier' },
    { id: 'conflit', label: 'Anti-double' },
  ];

  return (
    <div>
      <div className="mb-6">
        <h2 className="font-display text-xl font-bold" style={{ color: 'var(--admin-text)' }}>Gestion matériel</h2>
        <p className="text-xs" style={{ color: 'var(--admin-muted)' }}>Disponibilité, maintenance, calendrier, anti-double réservation</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 p-1 rounded-xl" style={{ background: 'var(--admin-surface)' }}>
        {TABS.map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)}
            className="flex-1 py-2 rounded-lg text-xs font-medium transition-all"
            style={{ background: activeTab === t.id ? '#D4AF37' : 'transparent', color: activeTab === t.id ? '#0D0D0D' : 'rgba(255,255,255,0.5)' }}>
            {t.label}
          </button>
        ))}
      </div>

      {/* DASHBOARD */}
      {activeTab === 'dashboard' && (
        <div>
          {loading ? (
            <div className="flex justify-center py-12"><div className="animate-spin w-8 h-8 rounded-full" style={{ border: '2px solid #2A2A2A', borderTopColor: '#D4AF37' }} /></div>
          ) : dashboard && (
            <div>
              {/* KPI grid */}
              <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-6">
                {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
                  <div key={key} className="p-4 rounded-xl text-center" style={{ background: cfg.bg, border: `1px solid ${cfg.color}20` }}>
                    <div className="flex justify-center mb-2" style={{ color: cfg.color }}>{cfg.icon}</div>
                    <div className="text-2xl font-bold font-mono-ose" style={{ color: cfg.color }}>
                      {dashboard.par_statut?.[key] || 0}
                    </div>
                    <div className="text-xs" style={{ color: 'var(--admin-muted)' }}>{cfg.label}</div>
                  </div>
                ))}
              </div>

              {/* Alertes */}
              {dashboard.alertes?.sans_inspection_recente > 0 && (
                <div className="p-4 rounded-xl mb-4" style={{
                  background: dashboard.alertes.niveau === 'rouge' ? 'rgba(255,92,108,0.08)' : 'rgba(255,204,102,0.08)',
                  border: `1px solid ${dashboard.alertes.niveau === 'rouge' ? '#FF5C6C' : '#FFCC66'}30`
                }}>
                  <div className="flex items-center gap-2">
                    <AlertTriangle size={16} style={{ color: dashboard.alertes.niveau === 'rouge' ? '#FF5C6C' : '#FFCC66' }} />
                    <span className="text-sm font-semibold" style={{ color: dashboard.alertes.niveau === 'rouge' ? '#FF5C6C' : '#FFCC66' }}>
                      {dashboard.alertes.sans_inspection_recente} produit(s) sans inspection depuis +30 jours
                    </span>
                  </div>
                </div>
              )}

              {/* En maintenance */}
              {dashboard.en_maintenance?.length > 0 && (
                <div className="p-4 rounded-xl" style={{ background: 'var(--admin-surface)', border: '1px solid #2A2A2A' }}>
                  <h3 className="font-semibold text-sm mb-3" style={{ color: '#FFCC66' }}>En maintenance</h3>
                  {dashboard.en_maintenance.map((p, i) => (
                    <div key={`col-${i}`} className="flex items-center justify-between py-2" style={{ borderBottom: '1px solid rgba(42,42,42,0.5)' }}>
                      <div>
                        <p className="text-sm font-medium" style={{ color: 'var(--admin-text)' }}>{p.nom}</p>
                        <p className="text-xs" style={{ color: 'var(--admin-muted)' }}>{p.maintenance_reason || 'Raison non spécifiée'}</p>
                      </div>
                      <button
                        onClick={() => updateStatus(p.id, 'disponible')}
                        className="px-3 py-1 rounded-lg text-xs" style={{ background: 'rgba(61,220,151,0.12)', color: '#3DDC97' }}>
                        Marquer OK
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* PRODUCTS STATUS */}
      {activeTab === 'products' && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <p className="text-sm" style={{ color: 'var(--admin-muted)' }}>{products.length} produits de location</p>
            <button onClick={fetchDashboard} className="p-2 rounded-lg" style={{ background: 'var(--admin-surface)', color: 'var(--admin-gold)' }}><RefreshCw size={16} /></button>
          </div>
          <div className="rounded-xl overflow-hidden" style={{ border: '1px solid #2A2A2A' }}>
            {products.map((p, i) => {
              const status = p.equipment_status || 'disponible';
              const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.disponible;
              return (
                <div key={p.id} className="flex items-center gap-3 px-4 py-3" style={{ background: i % 2 === 0 ? '#161616' : '#121212', borderBottom: '1px solid rgba(42,42,42,0.5)' }}>
                  <img src={p.images?.[0] || 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=60&q=50'}
                    alt={p.nom} className="w-10 h-10 rounded-lg object-cover flex-shrink-0" loading="lazy" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold truncate" style={{ color: 'var(--admin-text)' }}>{p.nom}</p>
                    <p className="text-xs" style={{ color: 'var(--admin-muted)' }}>
                      Sorties: {p.usage_count || 0} | Dernier contrôle: {p.last_check ? formatDate(p.last_check) : 'Jamais'}
                    </p>
                  </div>
                  <span className="px-2 py-1 rounded-full text-xs font-medium" style={{ background: cfg.bg, color: cfg.color }}>{cfg.label}</span>
                  <div className="flex gap-1">
                    {['disponible', 'maintenance', 'hors_service'].map(s => (
                      s !== status && (
                        <button key={s}
                          onClick={() => updateStatus(p.id, s)}
                          disabled={updating === p.id}
                          className="px-2 py-1 rounded-lg text-xs"
                          style={{ background: STATUS_CONFIG[s].bg, color: STATUS_CONFIG[s].color, opacity: updating === p.id ? 0.5 : 1 }}>
                          {s === 'disponible' ? 'OK' : s === 'maintenance' ? 'Maint.' : 'HS'}
                        </button>
                      )
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* CALENDAR */}
      {activeTab === 'calendar' && (
        <div>
          <h3 className="font-semibold text-sm mb-4" style={{ color: 'var(--admin-gold)' }}>
            {calendar ? `${calendar.mois}/${calendar.annee} — ${calendar.nb_reservations} réservation(s)` : 'Chargement...'}
          </h3>
          {calendar?.reservations?.length === 0 ? (
            <div className="p-8 text-center rounded-xl" style={{ background: 'var(--admin-surface)', border: '1px solid #2A2A2A' }}>
              <p className="text-sm" style={{ color: 'var(--admin-faint)' }}>Aucune réservation ce mois</p>
            </div>
          ) : (
            <div className="space-y-2">
              {calendar?.reservations?.map((r, i) => (
                <div key={`slot-${i}`} className="p-4 rounded-xl" style={{ background: 'var(--admin-surface)', border: '1px solid #2A2A2A' }}>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-mono-ose font-bold text-sm" style={{ color: 'var(--admin-gold)' }}>{r.date}</p>
                      <p className="text-xs" style={{ color: 'var(--admin-text)' }}>{r.client}</p>
                      <p className="text-xs" style={{ color: 'var(--admin-muted)' }}>{r.adresse}</p>
                      <p className="text-xs" style={{ color: 'var(--admin-muted)' }}>{r.items?.join(', ')}</p>
                    </div>
                    <span className="px-2 py-1 rounded-full text-xs" style={{ background: 'rgba(212,175,55,0.12)', color: 'var(--admin-gold)' }}>{r.statut}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ANTI-DOUBLE */}
      {activeTab === 'conflit' && (
        <div>
          <p className="text-sm mb-4" style={{ color: 'rgba(255,255,255,0.6)' }}>Vérifiez si des produits sont déjà réservés pour une date.</p>
          <div className="flex gap-3 mb-6">
            <input
              type="date"
              value={conflitCheck.date}
              onChange={e => setConflitCheck({ date: e.target.value, results: null })}
              min={new Date().toISOString().split('T')[0]}
              className="flex-1 px-3 py-2.5 rounded-xl text-sm"
              style={{ background: '#1C1C1C', border: '1px solid #2A2A2A', color: 'var(--admin-text)' }}
            />
            <button
              onClick={() => checkConflits(conflitCheck.date)}
              disabled={!conflitCheck.date}
              className="btn-gold px-5 py-2.5 rounded-xl text-sm font-semibold disabled:opacity-40"
            >
              Vérifier
            </button>
          </div>
          {conflitCheck.results && (
            <div className="p-4 rounded-xl" style={{
              background: conflitCheck.results.conflict ? 'rgba(255,92,108,0.08)' : 'rgba(61,220,151,0.08)',
              border: `1px solid ${conflitCheck.results.conflict ? '#FF5C6C' : '#3DDC97'}30`
            }}>
              <p className="font-semibold text-sm" style={{ color: conflitCheck.results.conflict ? '#FF5C6C' : '#3DDC97' }}>
                {conflitCheck.results.message}
              </p>
              {conflitCheck.results.conflits?.map((c, i) => (
                <div key={`msg-${i}`} className="mt-2 text-xs" style={{ color: 'rgba(255,255,255,0.6)' }}>
                  ⚠️ {c.product_nom} déjà réservé — Commande: {c.order_numero}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
